import React, {CSSProperties, useCallback, useMemo} from 'react'
import styled from 'styled-components'
import {
  GIFT_SHOP_TRANSACTION_BUILT_IN_KEY_WORD,
  GIFTSHOP_TRANSACTION_DETAIL_DESKTOP_WIDTH,
  GIFTSHOP_TRANSACTION_DETAIL_TABLET_WIDTH,
  WINDOW_MODE_MOBILE_WIDTH,
  WINDOW_MODE_TABLET_WIDTH,
} from 'consts'
import {useTranslation} from 'i18n'
import {
  getCurrencyValue,
  getThemeColor,
  parseDate,
  showSnackbar,
  useHistory,
  useTransactionDetailBuySellHooks,
} from 'utils'
import {GiftShopContentData, GiftShopInvoiceDetail} from 'types'
import {ButtonIcon, Icon, IconFirstBuy, Paragraph} from 'common/components'
import {useSelector} from 'lib/redux'
import convertUnit from 'lib/unit'
import {TransactionDetailContentSellerProps} from './TransactionDetailContentProps'
import {TransactionDetailContentHeader} from '../ContentHeader'
import {TransactionDetailContentItemSingleCreator} from '../ContentItem'
import TransactionDetailPackageItemSeller from '../ContentItem/TransactionDetailPackageItemSeller'

interface StyledButtonIconProps {
  backgroundColorOpacity?: number
  hasBackground?: boolean
  hasDefaultMargin?: boolean
}

const StyledContainer = styled.div<CSSProperties>`
  display: flex;
  width: ${convertUnit(GIFTSHOP_TRANSACTION_DETAIL_DESKTOP_WIDTH)};
  margin: ${convertUnit(25)};
  padding: ${convertUnit(25)} 0;
  border-radius: ${convertUnit(8)};
  background-color: ${({theme}) => theme.white_1};
  align-self: center;
  overflow: auto;

  @media (max-width: ${WINDOW_MODE_TABLET_WIDTH}px) {
    width: ${convertUnit(GIFTSHOP_TRANSACTION_DETAIL_TABLET_WIDTH)};
    flex-direction: column;
    padding: ${convertUnit(25)};
  }
  @media (max-width: ${WINDOW_MODE_MOBILE_WIDTH}px) {
    width: calc(100% - ${convertUnit(50)});
    min-height: 100%;
    margin: 0;
  }
`

const StyledContentSection = styled.div<CSSProperties>`
  display: flex;
  width: ${({width}) => width};
  flex-direction: column;
  margin: 0 ${convertUnit(25)};
  @media (max-width: ${WINDOW_MODE_TABLET_WIDTH}px) {
    width: 100%;
    margin-left: 0;
    margin-right: 0;
  }
`

const StyledInfoContainer = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  margin-right: ${convertUnit(25)};
  @media (max-width: ${WINDOW_MODE_MOBILE_WIDTH}px) {
    margin-right: 0;
  }
`

const StyledInfoItem = styled.div`
  width: 50%;
  margin-bottom: ${convertUnit(16)};
`

const StyledInfoItemFull = styled.div`
  width: 100%;
  margin-bottom: ${convertUnit(16)};
`

const StyledPricingContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${convertUnit(25)} 0;
  margin-bottom: ${convertUnit(40)};
  border-top: 1px solid ${({theme}) => theme.gray_1};
  border-bottom: 1px solid ${({theme}) => theme.gray_1};

  @media (max-width: ${WINDOW_MODE_MOBILE_WIDTH}px) {
    margin-left: ${convertUnit(-25)};
    margin-right: ${convertUnit(-25)};
    padding: ${convertUnit(25)};
    background: ${({theme}) => theme.white_3};
  }
`

const StyledPricing = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`

const StyledSeparatorContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
`

const StyledSeparator = styled.div`
  background-color: ${({theme}) => theme.gray_3};
  display: flex;
  flex: 1;
  height: ${convertUnit(1)};
  margin-top: ${convertUnit(10)};
`

const StyledSeparatorText = styled(Paragraph)`
  margin-left: ${convertUnit(5)};
  margin-bottom: ${convertUnit(10)};
`

const StyledTextHeader = styled(Paragraph)`
  margin-bottom: ${convertUnit(20)};
`

const StyledText = styled(Paragraph)`
  white-space: pre-wrap;
  margin-bottom: ${convertUnit(8)};
`

const StyledSuperscript = styled.span`
  vertical-align: super;
  font-size: smaller;
`

const StyledSettlementDetail = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: ${convertUnit(4)};
`

const StyledButtonIcon = styled(ButtonIcon)<StyledButtonIconProps>`
  ${({
    theme,
    backgroundColor = 'black',
    backgroundColorOpacity = 0.25,
    backgroundHoverColor = 'primary_5',
    hasBackground = true,
  }) => ({
    backgroundColor: hasBackground
      ? getThemeColor(theme, backgroundColor, backgroundColorOpacity)
      : 'transparent',
    ':hover': {
      backgroundColor: getThemeColor(theme, backgroundHoverColor, 0.25),
    },
  })}
  margin-left: ${({hasDefaultMargin = true}) =>
    hasDefaultMargin ? convertUnit(8) : undefined};
  max-width: ${convertUnit(42)};
`

const StyledInvoiceIdContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${convertUnit(4)};
`

const StyledCopyIcon = styled(Icon)`
  width: ${convertUnit(8)};
  height: ${convertUnit(8)};
  margin-right: ${convertUnit(8)};
  cursor: pointer;
`

const StyledTransactionItemsContainer = styled.div<CSSProperties>`
  display: flex;
  flex-direction: column;
  margin: 0 ${convertUnit(25)} ${convertUnit(20)} ${convertUnit(25)};
  gap: 0;
  @media (max-width: ${WINDOW_MODE_TABLET_WIDTH}px) {
    margin: 0;
  }
`

const StyledTransactionItemsPackage = styled.div`
  margin-bottom: ${convertUnit(10)};
`

export default function TransactionDetailContentSeller({
  stateData,
  isHostTransaction,
  transactionType: transaction_type,
}: TransactionDetailContentSellerProps) {
  const {translate} = useTranslation()
  const history = useHistory()

  const {
    invoice_transactions,
    price_detail,
    status: stateDataStatus,
    payment_at,
    payment_method: transaction_method,
    checkout_at,
    settled_at,
  } = stateData
  const singleInvoiceId = invoice_transactions[0].invoice_id
  const {discount} = price_detail
  const user = useSelector('user')!
  const {isHost, userBuysTheirOwnContent} = useTransactionDetailBuySellHooks(
    invoice_transactions[0].content,
    user,
  )

  const handleClickItemCreator = useCallback(
    (
      invoice: GiftShopInvoiceDetail,
      item: GiftShopContentData,
      data: GiftShopContentData[],
    ) => {
      history.push(
        'giftshop_profile_creation_detail',
        {
          data: data.map((datum) => ({
            ...datum,
            creator_id: invoice.creator_id,
            creator_name: invoice.creator_name,
          })),
          selectedItemId: item.content_id,
          limitContent: true,
        },
        item.creator_name,
      )
    },
    [history],
  )

  const handleChatCollector = useCallback(() => {
    if (!invoice_transactions[0].collector_member_id) return

    history.push('giftshop_message_room', {
      participantId: invoice_transactions[0].collector_member_id,
      selfRole: 'seller',
      roomType: 'giftshop',
    })
  }, [history, invoice_transactions])

  const handleSeparator = useMemo(
    () => (
      <StyledSeparatorContainer>
        <StyledSeparator />
        <StyledSeparatorText fontWeight="bold" color="gray_3">
          {'+'}
        </StyledSeparatorText>
      </StyledSeparatorContainer>
    ),
    [],
  )

  const fotoyuFeeBehindDecimal = useMemo(
    () =>
      Math.round(
        (price_detail.fotoyu_fee.percentage -
          Math.floor(price_detail.fotoyu_fee.percentage)) *
          10,
      ),
    [price_detail.fotoyu_fee],
  )

  const fotoyuFeeWholeNum = useMemo(
    () => Math.floor(price_detail.fotoyu_fee.percentage),
    [price_detail.fotoyu_fee],
  )

  const renderPriceBreakdown = useMemo(
    () => (
      <StyledPricingContainer>
        <StyledTextHeader fontWeight="bold" fontSize="l">
          {translate('giftShop:priceBreakdown')}
        </StyledTextHeader>
        <StyledPricing>
          <StyledText fontSize="m" fontWeight="medium">
            {translate('giftShop:priceNet', {
              count: price_detail?.net_creator.percentage,
            })}
          </StyledText>
          <StyledText
            fontSize="m"
            fontWeight={isHostTransaction ? 'regular' : 'bold'}
            color={isHostTransaction ? 'black' : 'primary_5'}>
            {getCurrencyValue(price_detail?.net_creator.nominal)}
          </StyledText>
        </StyledPricing>
        <StyledPricing>
          <StyledText>
            {`${translate(
              'giftShop:pricefee_forkyTitle',
            )} (${fotoyuFeeWholeNum}`}
            <StyledSuperscript>{`.${fotoyuFeeBehindDecimal}`}</StyledSuperscript>
            {`%)`}
          </StyledText>
          <StyledText fontWeight="bold">
            {getCurrencyValue(price_detail.fotoyu_fee.nominal)}
          </StyledText>
        </StyledPricing>

        {isHost && (
          <StyledPricing>
            <StyledText fontSize="m" fontWeight="medium">
              {translate('giftShop:pricefee_host', {
                count: price_detail?.host_fee.percentage,
              })}
            </StyledText>
            <StyledText
              fontSize="m"
              fontWeight={isHostTransaction ? 'bold' : 'regular'}
              color={isHostTransaction ? 'primary_5' : 'black'}>
              {getCurrencyValue(price_detail?.host_fee.nominal)}
            </StyledText>
          </StyledPricing>
        )}
        {handleSeparator}
        <StyledPricing>
          <StyledText fontSize="m" fontWeight="medium">
            {translate('giftShop:pricefee', {
              count: price_detail?.creator.percentage,
            })}
          </StyledText>
          <StyledText fontSize="m">
            {getCurrencyValue(price_detail?.creator.nominal)}
          </StyledText>
        </StyledPricing>
        <StyledPricing>
          <Paragraph fontSize="m" fontWeight="medium">
            {translate('giftShop:pricefee_paymentservice')}
          </Paragraph>
          <Paragraph fontSize="m">
            {getCurrencyValue(price_detail?.service_fee.nominal)}
          </Paragraph>
        </StyledPricing>
        <StyledText fontSize="m" fontWeight="medium">
          {translate('giftShop:pricefee_paymentservicepercentage', {
            count: price_detail?.service_fee.percentage,
          })}
        </StyledText>
        {handleSeparator}
        <StyledPricing>
          <StyledText fontSize="m" fontWeight="medium">
            {translate('giftShop:collectorPay')}
          </StyledText>
          <StyledText fontSize="m" fontWeight="bold" color="primary_5">
            {getCurrencyValue(price_detail?.final.nominal)}
          </StyledText>
        </StyledPricing>
      </StyledPricingContainer>
    ),
    [
      fotoyuFeeBehindDecimal,
      fotoyuFeeWholeNum,
      handleSeparator,
      isHost,
      isHostTransaction,
      price_detail,
      translate,
    ],
  )

  const handlePaymentMethod = useMemo(() => {
    if (transaction_method !== GIFT_SHOP_TRANSACTION_BUILT_IN_KEY_WORD) {
      return transaction_method || 'OVO'
    }
    return translate('global:free')
  }, [transaction_method, translate])

  const handleRenderMessage = useMemo(
    () =>
      !userBuysTheirOwnContent && (
        <StyledButtonIcon
          hasShadow={false}
          hasBackground={false}
          iconType="message"
          iconColor="primary_5"
          onClick={handleChatCollector}
        />
      ),
    [handleChatCollector, userBuysTheirOwnContent],
  )

  const copyInvoiceToClipboard = useCallback(() => {
    navigator.clipboard.writeText(singleInvoiceId)
    showSnackbar(translate('global:invoiceCopied'))
  }, [singleInvoiceId, translate])

  const renderPackageItems = useMemo(
    () =>
      invoice_transactions[0].package.length > 0 ? (
        <StyledTransactionItemsPackage>
          <Paragraph fontWeight="bold" fontSize="xl">
            {translate('giftShop:packageDetail')}
          </Paragraph>
          {invoice_transactions[0].package.map((packageData) => (
            <TransactionDetailPackageItemSeller
              key={packageData.tag_id}
              content={packageData}
            />
          ))}
        </StyledTransactionItemsPackage>
      ) : (
        <></>
      ),
    [invoice_transactions, translate],
  )

  const renderContentItems = useMemo(
    () =>
      invoice_transactions[0].content.length > 0 ? (
        <div>
          <Paragraph fontWeight="bold" fontSize="xl">
            {translate('giftShop:contentDetail')}
          </Paragraph>
          {invoice_transactions[0].content.map((contentData) => (
            <TransactionDetailContentItemSingleCreator
              key={contentData.content_id}
              content={contentData}
              discount={discount}
              onClickItem={() =>
                handleClickItemCreator(
                  invoice_transactions[0],
                  contentData,
                  invoice_transactions[0].content,
                )
              }
              showDiscount
              showPackage={contentData?.is_package}
            />
          ))}
        </div>
      ) : (
        <></>
      ),
    [discount, handleClickItemCreator, invoice_transactions, translate],
  )

  const renderContent = useMemo(
    () => (
      <>
        <StyledContentSection width={'50%'}>
          <TransactionDetailContentHeader
            title={translate('giftShop:transactionType', {
              context: transaction_type.toUpperCase(),
            })}
            subTitle={invoice_transactions[0].collector_name}
            statuses={[
              {
                status: stateDataStatus,
              },
            ]}
            titleRightElement={handleRenderMessage}
            subtitleRightElement={
              invoice_transactions[0].is_free_first_buy && (
                <IconFirstBuy text={translate('giftShop:firstPromo')} />
              )
            }
          />
          <StyledInfoContainer>
            {stateDataStatus === 'PENDING' && !!settled_at && (
              <StyledInfoItemFull>
                <Paragraph fontSize="m" fontWeight="medium">
                  {translate('giftShop:transactionSettlementTime')}
                </Paragraph>
                <StyledSettlementDetail>
                  <Paragraph fontSize="m" fontWeight="medium" color="primary_5">
                    {parseDate(settled_at, 'MMMM D, YYYY, HH:mm')}
                  </Paragraph>
                  <Paragraph fontSize="m" color="gray_5">
                    {translate('giftShop:transactionSettlementTimeDescription')}
                  </Paragraph>
                </StyledSettlementDetail>
              </StyledInfoItemFull>
            )}
            {checkout_at && (
              <StyledInfoItem>
                <Paragraph fontSize="m" fontWeight="medium">
                  {translate('giftShop:transactionTime')}
                </Paragraph>
                <Paragraph fontSize="m">
                  {(checkout_at &&
                    parseDate(checkout_at, 'MMMM D, YYYY, HH:mm')) ||
                    ''}
                </Paragraph>
              </StyledInfoItem>
            )}
            {payment_at && (
              <StyledInfoItem>
                <Paragraph fontSize="m" fontWeight="medium">
                  {translate('giftShop:transactionPaymentTime')}
                </Paragraph>
                <Paragraph fontSize="m">
                  {(payment_at &&
                    parseDate(payment_at, 'MMMM D, YYYY, HH:mm')) ||
                    ''}
                </Paragraph>
              </StyledInfoItem>
            )}

            <StyledInfoItem>
              <Paragraph fontSize="m" fontWeight="medium">
                {translate('giftShop:paymentMethod')}
              </Paragraph>
              <Paragraph fontSize="m">{handlePaymentMethod}</Paragraph>
            </StyledInfoItem>

            {invoice_transactions.length === 1 && (
              <StyledInfoItem>
                <StyledInvoiceIdContainer>
                  <Paragraph fontSize="m" fontWeight="medium">
                    {translate('giftShop:invoiceID')}
                  </Paragraph>
                  <StyledCopyIcon
                    type="copy"
                    onClick={copyInvoiceToClipboard}
                    color="primary_5"
                    size={12}
                  />
                </StyledInvoiceIdContainer>
                <Paragraph
                  fontSize="m"
                  fontWeight="bold"
                  color="primary_5"
                  onClick={copyInvoiceToClipboard}>
                  {singleInvoiceId}
                </Paragraph>
              </StyledInfoItem>
            )}
          </StyledInfoContainer>
          {renderPriceBreakdown}
        </StyledContentSection>
        <StyledTransactionItemsContainer>
          {renderPackageItems}
          {renderContentItems}
        </StyledTransactionItemsContainer>
      </>
    ),
    [
      translate,
      transaction_type,
      invoice_transactions,
      stateDataStatus,
      handleRenderMessage,
      settled_at,
      checkout_at,
      payment_at,
      handlePaymentMethod,
      copyInvoiceToClipboard,
      singleInvoiceId,
      renderPriceBreakdown,
      renderPackageItems,
      renderContentItems,
    ],
  )

  return <StyledContainer>{renderContent}</StyledContainer>
}
